import { IntlMessageFormat } from "intl-messageformat";

/**
 * The i18n framework: message keys, ICU formatting, and what happens when a
 * translation is missing.
 *
 * Three rules from `10/06` §3 shape everything here, and each exists because
 * the obvious alternative breaks Arabic specifically.
 *
 * **No concatenation.** A sentence is one key with placeholders, never two
 * strings joined with a `+`. Arabic word order is not English word order, so
 * `"Showing " + n + " of " + total` is untranslatable: the translator gets three
 * fragments and no way to reorder them. One key, one sentence, and the
 * placeholders move inside it.
 *
 * **ICU plurals, all six categories.** Arabic has `zero`, `one`, `two`, `few`,
 * `many` and `other` — 0, 1, 2, 3-10, 11-99, 100+ — where English has two. A
 * `n === 1 ? singular : plural` in the code cannot express that, which is why
 * the plural lives in the message and the code passes a number.
 *
 * **Missing keys fall back to English and say so, once.** A blank label is a
 * bug report that takes a week to trace; an English label in an Arabic screen
 * is obvious to anybody looking at it, and the structured warning names the key
 * so it can be fixed without reproducing it. Once per key, because a missing
 * key in a table cell would otherwise log a line per row.
 */

/** The locales the console is built in. Matches `preference.Locales()` in the API. */
export const LOCALES = ["en", "ar"] as const;

/** One of the supported locales. */
export type Locale = (typeof LOCALES)[number];

/** What somebody who has never chosen gets. */
export const DEFAULT_LOCALE: Locale = "en";

/** Reading direction. */
export type Direction = "ltr" | "rtl";

/**
 * A flat bag of message keys.
 *
 * Flat, not nested, so a key is one string in the source and one string in the
 * JSON — `t("ui.table.showing")` greps to exactly one line in each bundle. A
 * nested tree makes the same lookup a path expression and turns a typo into an
 * `undefined` two levels down.
 */
export type Messages = Readonly<Record<string, string>>;

/**
 * The direction a locale reads in.
 *
 * Derived rather than stored, because the two cannot disagree: there is no
 * Arabic that reads left to right, and a stored `dir` is one more thing that can
 * be wrong.
 */
export function directionOf(locale: Locale): Direction {
	return locale === "ar" ? "rtl" : "ltr";
}

/** Whether a string names a locale this build has. */
export function isLocale(value: unknown): value is Locale {
	return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

// ── bundles ─────────────────────────────────────────────────────────────────
//
// Three globs, because the strings live in three kinds of place and each has a
// different owner: a module owns its own screens' words, `@khazzin/ui` owns the
// ones every screen shares ("Save", "Cancel", "Showing 1-20 of 340"), and
// `app/` owns the shell's — the No-access screen, the reload banner — which
// belong to neither.
//
// Vite resolves these at build time, so adding `modules/orders/i18n/ar.json`
// registers it with no edit here. That is the point: a story that adds a screen
// adds its words beside it, and nothing central has to know.
//
// The module and shell globs start with `/`, which Vite resolves against the
// *building app's* root rather than against this file (EP10-S01). This package
// is compiled as source into each of the three apps, so one pattern gives each
// app exactly its own bundles — the Admin Console never ships the Driver App's
// words, and no app has to register anything. The design system's bundle is
// the one fixed path, because every app shares it.

const eagerEnglish = import.meta.glob<{ default: Messages }>(
	["/src/modules/*/i18n/en.json", "../../ui/src/i18n/en.json", "/src/app/*/i18n/en.json"],
	{ eager: true },
);

// English is excluded here, and the exclusion is load-bearing rather than
// tidiness. A file that is both eagerly and lazily imported cannot be split
// out — Rollup says so out loud — so leaving `en.json` in this glob would put
// every English bundle in a lazy chunk that also stays in the main one, and the
// build would warn on every locale file for the life of the project.
const lazyBundles = import.meta.glob<{ default: Messages }>([
	"/src/modules/*/i18n/*.json",
	"../../ui/src/i18n/*.json",
	"/src/app/*/i18n/*.json",
	"!**/en.json",
	// Named as well, because the unanchored exclusion above does not reach a
	// file outside the building app's root.
	"!../../ui/src/i18n/en.json",
]);

/**
 * Keys that are not messages.
 *
 * `_review` marks a bundle as machine-drafted and pending a native reader. It
 * is metadata about the file rather than a string anybody renders, and
 * `check-i18n` reads it to decide whether a gap is a warning or a failure.
 */
const METADATA_KEYS = new Set(["_review"]);

function merge(bundles: Record<string, { default: Messages }>): Messages {
	const out: Record<string, string> = {};
	for (const module of Object.values(bundles)) {
		for (const [key, value] of Object.entries(module.default)) {
			if (!METADATA_KEYS.has(key) && typeof value === "string") {
				out[key] = value;
			}
		}
	}
	return out;
}

/**
 * English, bundled eagerly and never loaded over the network.
 *
 * `10/06` §9: a bundle that fails to load must fall back to embedded English
 * rather than to nothing. That only works if English is *in* the main chunk —
 * a lazily loaded fallback has the same failure mode as the thing it is meant
 * to rescue.
 */
export const ENGLISH: Messages = merge(eagerEnglish);

/**
 * Loads a locale's messages.
 *
 * English resolves synchronously from the eager bundle. Anything else is one
 * lazy chunk per locale, and a chunk that fails to arrive resolves to English
 * rather than rejecting: a translation the network lost is a screen in the
 * wrong language, not a screen that does not render.
 */
export async function loadMessages(locale: Locale): Promise<Messages> {
	if (locale === DEFAULT_LOCALE) {
		return ENGLISH;
	}

	const loaders = Object.entries(lazyBundles).filter(([path]) => path.endsWith(`/${locale}.json`));

	const loaded = await Promise.all(
		loaders.map(async ([path, load]) => {
			try {
				return await load();
			} catch (cause) {
				warn("i18n.bundle_failed", { path, locale, cause: String(cause) });
				return { default: {} as Messages };
			}
		}),
	);

	// English underneath, the locale on top. A partially translated bundle then
	// falls back key by key rather than all at once, which is what makes it
	// possible to ship a translation before it is finished.
	return { ...ENGLISH, ...merge(Object.fromEntries(loaded.map((m, i) => [String(i), m]))) };
}

// ── formatting ──────────────────────────────────────────────────────────────

/** Values substituted into a message's placeholders. */
export type Values = Record<string, string | number | Date>;

/** Translates a key. */
export type Translate = (key: string, values?: Values) => string;

/** Keys already warned about, so a table of 200 rows logs once rather than 200 times. */
const warned = new Set<string>();

/** Resets the warned-key memo. Exported for tests, which need each case isolated. */
export function resetMissingKeyWarnings(): void {
	warned.clear();
}

function warn(event: string, fields: Record<string, unknown>): void {
	// Structured, because this is read in aggregate: "which keys are missing in
	// ar" is a question somebody answers by grepping a log, not by reading one
	// line. `10/06` §8 names the shape.
	console.warn(JSON.stringify({ event, ...fields }));
}

/**
 * Whether to render a missing key's name or an ellipsis.
 *
 * In development the key itself, because the person looking at the screen is
 * the person who can add it and `ui.table.showing` tells them exactly what to
 * add. In production an ellipsis, because a customer should not be shown our
 * internal vocabulary — and a key rendered in a cell is worse than a gap: it
 * looks like corrupt data rather than a missing translation.
 */
const isDev = import.meta.env.DEV;

/**
 * Builds a translator over one locale's messages.
 *
 * The compiled-format cache is the reason this is a factory rather than a bare
 * function. `IntlMessageFormat` parses the ICU string into an AST on
 * construction, which is the expensive half; a table rendering the same
 * "Showing {shown} of {total}" for every page would otherwise re-parse it on
 * every render. The cache is per (locale, messages) pair, so switching language
 * discards it rather than serving Arabic plurals from an English AST.
 */
export function createTranslate(
	locale: Locale,
	messages: Messages,
	numerals: NumeralSystem = "latn",
): Translate {
	const compiled = new Map<string, IntlMessageFormat>();
	// Not the bare locale: a `#` inside an Arabic plural would render in
	// Arabic-Indic digits, which is precisely what `10/06` §3 says not to do.
	const formatting = intlLocale(locale, numerals);

	return (key, values) => {
		const template = messages[key] ?? ENGLISH[key];

		if (template === undefined) {
			// Missing everywhere, including English. Something asked for a key
			// nobody ever wrote.
			warnOnce(key, locale, "i18n.unknown_key");
			return isDev ? key : "…";
		}
		if (messages[key] === undefined) {
			// Present in English, absent in this locale. The screen renders,
			// in the wrong language, and the log says which key to translate.
			warnOnce(key, locale, "i18n.missing_key");
		}

		// A message with no placeholders is the common case by a wide margin, and
		// running it through the ICU parser to get its own text back is pure
		// cost. The brace test is the cheapest possible check for "is this
		// actually a template".
		if (values === undefined && !template.includes("{")) {
			return template;
		}

		try {
			let format = compiled.get(key);
			if (format === undefined) {
				format = new IntlMessageFormat(template, formatting);
				compiled.set(key, format);
			}
			return String(format.format(values));
		} catch (cause) {
			// A malformed ICU string, or a value the message did not expect.
			// Neither is worth taking a screen down for: show the raw template,
			// which at least carries the words.
			warn("i18n.format_failed", { key, locale, cause: String(cause) });
			return template;
		}
	};
}

function warnOnce(key: string, locale: Locale, event: string): void {
	const memo = `${locale}:${key}`;
	if (warned.has(memo)) {
		return;
	}
	warned.add(memo);
	warn(event, { key, locale });
}

// ── numerals ────────────────────────────────────────────────────────────────

/** The digit sets a number can be rendered in. */
export type NumeralSystem = "latn" | "arab";

/**
 * Which digits to render numbers in.
 *
 * **`latn` even in Arabic**, and that is `10/06` §3 rather than an oversight.
 * Money, quantities and identifiers stay in 0-9 because a warehouse reads a SKU
 * off a label printed in one script and types it into a screen rendered in
 * another, and a picker comparing `١٠٤٢` on screen against `1042` on a carton
 * is doing a translation the system should have done for them.
 *
 * The tenant can ask for `arab` — the field is on `/me/context` and reserved —
 * and when it does, this is the one place that changes.
 */
export function numeralsFor(orgSetting: string | undefined): NumeralSystem {
	return orgSetting === "arab" ? "arab" : "latn";
}

/**
 * The locale tag to hand to `Intl`, carrying the digit set.
 *
 * This is the load-bearing half of the Latin-digits rule, and it is easy to
 * miss because nothing looks wrong without it. `new Intl.NumberFormat("ar")`
 * renders `٥` — Arabic-Indic digits are the CLDR default for the locale — and so
 * does every `#` inside an ICU plural formatted with `"ar"`. Passing the bare
 * locale anywhere means the digits quietly switch script in Arabic while the
 * code that asked for the number has no idea.
 *
 * The `-u-nu-` extension pins the digits, so one locale tag carries both the
 * language and the numbering system and there is no second parameter for a
 * caller to forget.
 */
export function intlLocale(locale: Locale, numerals: NumeralSystem = "latn"): string {
	return `${locale}-u-nu-${numerals}`;
}

// ── the document ────────────────────────────────────────────────────────────

import { KEYS, readOneOf, writeString } from "./storage";

/**
 * The locale this browser last used.
 *
 * The pre-session fallback. `context.preferences.locale` is the source of truth
 * and follows somebody between devices, but it is a network round trip away —
 * and `dir` has to be on the document before the first paint or an Arabic
 * tenant renders left to right for one frame on every cold start.
 */
export function storedLocale(): Locale {
	return readOneOf(KEYS.locale, LOCALES, DEFAULT_LOCALE);
}

/** Remembers a locale for the next cold start. */
export function rememberLocale(locale: Locale): void {
	writeString(KEYS.locale, locale);
}

/**
 * Puts the locale on the document.
 *
 * Both attributes, and both matter. `dir` mirrors the layout — every logical
 * CSS property in the design system resolves against it, which is why there is
 * no second stylesheet for Arabic. `lang` is what a screen reader switches
 * voice on, what the browser hyphenates and spell-checks by, and what the API
 * client reads to set `Accept-Language`; a page labelled English while showing
 * Arabic is read aloud as gibberish.
 */
export function applyDocumentLocale(locale: Locale): void {
	const root = document.documentElement;
	root.lang = locale;
	root.dir = directionOf(locale);
}
